/**
 * Generate a campaign contract PDF using browser print.
 * Opens a new window with styled HTML and triggers print dialog (Save as PDF).
 */
import { INFLUENCER_PAYOUT_RATE } from '../constants/platform';

export function generateCampaignContractPdf(params: {
  campaign: any;
  userName: string;
  selectedMissions: string[];
  visitStartDate: string;
  visitEndDate: string;
  requestedAt?: string;
  respondedAt?: string;
}): void {
  const { campaign: c, userName, selectedMissions, visitStartDate, visitEndDate, requestedAt, respondedAt } = params;
  const pc = c.publishingConditions;

  const escape = (s: string | undefined | null) => {
    if (!s) return '';
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br/>');
  };

  const formatDate = (d: string | undefined) => {
    if (!d) return '-';
    return d.slice(0, 10);
  };

  // Build mission list
  const missions: string[] = [];
  if (selectedMissions.includes('blog') && pc?.blog?.conditions?.length > 0) {
    pc.blog.conditions.forEach((cond: any) => {
      const label = cond.item === 'blogPost' ? '리뷰 콘텐츠' : cond.item;
      missions.push(`블로그: ${label} ${cond.quantity}건 등록`);
    });
  }
  if (selectedMissions.includes('instagram') && pc?.instagram?.conditions?.length > 0) {
    pc.instagram.conditions.forEach((cond: any) => {
      const label = cond.item === 'reels' ? '릴스' : cond.item === 'story' ? '스토리' : '피드 게시물';
      missions.push(`SNS (인스타그램): ${label} ${cond.quantity}건 등록`);
    });
  }
  if (selectedMissions.includes('youtube') && pc?.youtube?.conditions?.length > 0) {
    pc.youtube.conditions.forEach((cond: any) => {
      const label = cond.item === 'video' ? '영상' : cond.item === 'shorts' ? '쇼츠' : cond.item;
      missions.push(`유튜브: ${label} ${cond.quantity}건 등록`);
    });
  }

  // Build secondary submissions
  const secondary = (c.secondarySubmissions || []).map((s: any) =>
    `${s.type === 'video' ? '영상' : '사진'} ${s.quantity}${s.type === 'video' ? '분' : '장'} 이상${s.note ? ` (${s.note})` : ''}`
  );

  const rewardText = c.rewardAmount
    ? `리워드: $${Math.round(c.rewardAmount * INFLUENCER_PAYOUT_RATE).toLocaleString()}`
    : '';

  const missionsHtml = missions.map(m => `<li>${escape(m)}</li>`).join('');
  const secondaryHtml = secondary.length > 0
    ? `<p class="sub-title">2차 제출</p><ul>${secondary.map((s: string) => `<li>${escape(s)}</li>`).join('')}</ul><p class="note">화질이 선명하고 흔들림이 없는 원본 퀄리티 유지</p>`
    : '';

  const html = `<!DOCTYPE html>
<html lang="ko">
<head>
<meta charset="UTF-8">
<title>캠페인 계약서 - ${escape(c.campaignName)}</title>
<style>
  @page { size: A4; margin: 20mm; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Malgun Gothic', 'Apple SD Gothic Neo', sans-serif; font-size: 11pt; line-height: 1.7; color: #222; padding: 40px; }
  h1 { text-align: center; font-size: 18pt; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 10px; }
  h2 { font-size: 12pt; margin: 24px 0 8px; color: #111; }
  p { margin: 4px 0; }
  ul { margin: 4px 0 4px 20px; }
  li { margin: 2px 0; }
  .article { margin-bottom: 16px; }
  .sub-title { font-weight: bold; margin-top: 8px; }
  .note { font-size: 10pt; color: #666; margin-top: 4px; }
  .field { margin: 4px 0; }
  .field-label { color: #555; font-size: 10pt; }
  .field-value { font-weight: 500; }
  .indent { margin-left: 16px; }
  .signatures { display: flex; justify-content: space-between; margin-top: 40px; padding-top: 20px; border-top: 1px solid #ccc; }
  .sig-block { width: 45%; }
  .sig-block h3 { font-size: 11pt; margin-bottom: 12px; border-bottom: 1px solid #999; padding-bottom: 4px; }
  .sig-block p { font-size: 10pt; margin: 4px 0; }
  @media print { body { padding: 0; } }
</style>
</head>
<body>
<h1>캠페인 계약서</h1>

<div class="article">
<h2>제1조 (목적)</h2>
<p>본 계약은 '${escape(c.brandName)}'(이하 '브랜드')가 제공하는 제품 또는 서비스를 인플루언서 '${escape(userName)}'(이하 '인플루언서')가 체험하고, 이를 본인의 소셜 미디어 채널에 성실히 홍보함으로써 '브랜드'의 브랜드 인지도를 높이고 마케팅 효과를 창출하는 것을 목적으로 한다.</p>
</div>

<div class="article">
<h2>제2조 (캠페인 개요)</h2>
<div class="field"><span class="field-label">캠페인명:</span> <span class="field-value">${escape(c.campaignName)}</span></div>
<div class="field"><span class="field-label">제공 서비스 내역:</span></div>
<div class="indent">
  ${c.productName ? `<p>${escape(c.productName)}</p>` : ''}
  ${c.providedValueDescription ? `<p>${escape(c.providedValueDescription)}</p>` : ''}
  ${rewardText ? `<p>${escape(rewardText)}</p>` : ''}
</div>
<div class="field"><span class="field-label">캠페인 기간:</span></div>
<div class="indent">
  <p>방문기간: ${escape(visitStartDate)} ~ ${escape(visitEndDate)}</p>
  <p>콘텐츠 등록 기간: 방문 후 ${c.uploadDeadlineDays || 7}일 이내</p>
</div>
</div>

<div class="article">
<h2>제3조 (콘텐츠 제작 및 미션)</h2>
<p>'인플루언서'는 아래의 미션을 준수하여 콘텐츠를 제작 및 게시해야 한다.</p>
<p class="sub-title">미션:</p>
<ul>${missionsHtml}</ul>
${secondaryHtml}
</div>

<div class="article">
<h2>제4조 (콘텐츠 가이드라인)</h2>
${c.contentWritingRequirements ? `<p>${escape(c.contentWritingRequirements)}</p>` : ''}
<p>'인플루언서'는 제공된 서비스의 특징을 충분히 부각하여 긍정적인 경험을 상세히 서술해야 한다.</p>
<p>타 브랜드의 로고나 제품이 직접적으로 노출되지 않도록 주의하며, 저작권 및 초상권에 위배되지 않는 순수 창작물을 게시한다.</p>
<p>게시물에는 '공정거래위원회' 지침에 따라 광고/협찬 사실을 명확히 표기해야 한다.</p>
</div>

<div class="article">
<h2>제5조 (게시물의 유지 및 사후 관리)</h2>
<p>'인플루언서'가 게시한 콘텐츠는 등록일로부터 최소 6개월 동안 삭제하거나 비공개로 전환할 수 없다.</p>
<p>'브랜드'는 '인플루언서'가 제작한 콘텐츠를 브랜드 공식 채널(인스타그램, 홈페이지, 마케팅 자료 등)에 2차 활용(재가공 포함)할 수 있는 권리를 가진다.</p>
</div>

<div class="article">
<h2>제6조 (위약금 및 계약의 해지)</h2>
<p>'인플루언서'가 정당한 사유 없이 콘텐츠 마감 기한을 준수하지 않거나 미션을 이행하지 않을 경우, 제공받은 서비스 비용 전액을 배상해야 한다.</p>
<p>'인플루언서' 계약 체결 후 단순 변심으로 노쇼(No-Show)를 하거나 콘텐츠를 삭제할 경우에도 동일한 배상 책임이 발생한다.</p>
<p>사회적 물의를 일으켜 브랜드 이미지에 타격을 주는 행위 시 계약은 즉시 해지될 수 있다.</p>
</div>

<div class="article">
<h2>제7조 (계약의 성립 및 효력 발생)</h2>
<p>본 계약은 계약서 초안을 전자적 수단(이메일, 메신저 등)으로 송부하고, "인플루언서"가 이에 대해 "확정진행"의 의사를 전자적으로 회신함과 동시에 성립된 것으로 간주합니다.</p>
<p>본 계약서 수신 후 7일 이내에 명시적인 거부 의사를 밝히지 않고, 본 계약에 명시된 업무(예: 자료 송부, 프로젝트 착수 등)를 개시하는 경우에도 본 계약의 조건에 동의한 것으로 간주합니다.</p>
<p>양 당사자는 위와 같은 전자적 의사표시가 전자문서 및 전자거래 기본법 및 전자서명법에 의거하여 실제 서명 날인과 동일한 법적 구속력을 가짐을 확인합니다.</p>
</div>

<div class="article">
<h2>제8조 (기타 사항)</h2>
<p>본 계약에 명시되지 않은 사항은 상호 협의하여 결정하며, 협의가 이루어지지 않을 경우 일반적인 상관례에 따른다.</p>
</div>

<div class="signatures">
  <div class="sig-block">
    <h3>[브랜드]</h3>
    <p>성명/법인명: ${escape(c.brandName)}</p>
    <p>날짜: ${formatDate(requestedAt)}</p>
  </div>
  <div class="sig-block">
    <h3>[인플루언서]</h3>
    <p>성명/닉네임: ${escape(userName)}</p>
    <p>날짜: ${formatDate(respondedAt)}</p>
  </div>
</div>

</body>
</html>`;

  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    alert('팝업이 차단되었습니다. 팝업 허용 후 다시 시도해주세요.');
    return;
  }
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.onload = () => { printWindow.print(); };
}
